import React, {useEffect, useRef} from 'react'
import * as Sentry from '@sentry/react'
import {useDispatch, useSelector} from 'react-redux'
import Pollable from '../lib/Pollable'
import {loadConversations} from '../actions/messagesActions'
import {loadAmenityReservationsForBuilding} from '../actions/amenitiesActions'
import {loadNudgesAndActionsForBuilding} from '../actions/nudgesActions'
import {loadVendorServicesForBuilding, loadAppointmentsForBuilding} from '../actions/vendorAppointmentsActions'
import {useLocation, matchPath} from 'react-router-dom'
import useSound from 'use-sound'
import newMessageTone from '../assets/audio/new_message.mp3'
import Routes from '../constants/RouteConstants'
import {setBuildingViewing} from '../actions/buildingsActions'
import {CONVERSATION_CLOSED} from '../constants/ModelConstants'
import {isResidentActive} from '../Utils/residentUtils'
import NewMessages from './Overlays/NewMessages'
import {MESSAGES_POLL_TIME_MS} from '../constants/AppConstants'
import {updateChatRoomStatus} from './Messages/chatContainerQueries'
import {loadBuildingStaffForBuilding} from '../actions/staffActions'
import {loadSisterBuildingData} from '../actions/sisterBuildingDataActions'
import {selectAuthedUserId, selectActiveBuildingAndSisterBuildingIds} from '../reducers/selectors'
import useResidentLookup from './hooks/useResidentLookup'

function BackgroundDataSync() {
  const pollables = useRef([])
  const permissionsObject = useSelector(state => state.GroupPermissions.permissionsObject)
  const authedStaffId = useSelector(selectAuthedUserId)
  const activeBuildingId = useSelector(state => state.Buildings.activeBuildingId)
  const buildingIds = useSelector(selectActiveBuildingAndSisterBuildingIds)
  const hasLoadedResidents = useSelector(state => state.Residents.hasLoadedResidentsAtBuilding)
  const latestResidentMessageTimestamp = useSelector(state => state.Messages.latestResidentMessageTimestamp)
  const isMuted = useSelector(state => state.App.muteSounds)
  const previousLastMessageTimestamp = useRef(undefined)
  const dispatch = useDispatch()
  const {pathname} = useLocation()
  const [playNewMessageTone] = useSound(newMessageTone)
  const {getResident} = useResidentLookup()

  useEffect(() => {
    pollables.current.forEach(p => p.stopPolling())

    if (!activeBuildingId || !authedStaffId || !hasLoadedResidents) {
      return
    }

    pollables.current = [
      // 2 minute - amenity reservations from all buildings (including sisters)
      new Pollable('AmenityReservations', 120000, () => loadAmenityReservationsForBuilding(dispatch, buildingIds)),

      // 2 minute
      new Pollable('RXOAssistant', 120000, () => loadNudgesAndActionsForBuilding(dispatch, activeBuildingId)),

      // 10 minutes
      new Pollable('VendorAppointments', 600000, () => loadAppointmentsForBuilding(dispatch, activeBuildingId)),

      // 10 minutes - active building data only
      new Pollable('BuildingData', 600000, () => setBuildingViewing(dispatch, activeBuildingId, activeBuildingId)),

      // 10 minutes - sister building data (for amenities/events) - includes staff
      new Pollable('SisterBuildingData', 600000, () => loadSisterBuildingData(dispatch, buildingIds.slice(1), activeBuildingId)),

      // 1 hour - load staff for active building only (sister building staff loaded via SisterBuildingData)
      new Pollable('StaffUsers', 3600000, () => loadBuildingStaffForBuilding(dispatch, activeBuildingId)),

      // 1 hour
      new Pollable('VendorServices', 3600000, () => loadVendorServicesForBuilding(dispatch, activeBuildingId)),
      // ----- add more pollables here
    ]

    if (permissionsObject.messagesRead) {
      let checkedForPastResidents = false
      pollables.current.push(
        // refresh messages every 5 seconds
        new Pollable('Messages', MESSAGES_POLL_TIME_MS, async () => {
          const conversations = await loadConversations(dispatch, activeBuildingId)

          // this is a little hacky. The idea is sometimes a resident will become inactive, but still have an open conversation
          // we don't want to include inactive residents in the list, but we also don't want their conversation status counted
          // in the nav panel. Ideally, we'd have some async process that ensures past resident conversations are set to CLOSED
          // but until then, we'll just update it when they come to the Messages page

          // ALSO, this code assumes every conversation that needs to close is a Conversation type (never ChatRoom).
          // Eventually, this assumption will be wrong. At that time we need to refactor 2 things:
          //  1. The loadConversations payload will need to return both ChatRooms and Conversation objects
          //  2. This code should be refactored to use GenericChatRoom models
          if (checkedForPastResidents) {
            // only want to check it once since it's kind of costly and not likely to change in a single day
            // and if it does they can just refresh to check it again
            return
          }

          const conversationsToClose = conversations.filter(c => {
            const res = getResident(c.conversationResidentId)
            return c.status !== CONVERSATION_CLOSED && (res._notFound || !isResidentActive(res))
          })

          if (conversationsToClose.length > 8) {
            // we've had a bug pop up a couple times over the years where this logic closes ALL conversations at once.
            // The first time it was due to the residents not being loaded, but I'm not sure how it could
            // have happened again since we now definitely wait for residents to have loaded first...
            // Moving forward, if it ever tries to close more than 8 at a time, we're going to bail and log it
            Sentry.withScope(scope => {
              scope.setExtra('activeBuildingId', activeBuildingId)
              scope.setExtra('conversationsCount', conversationsToClose.length)
              scope.setExtra('staffUserId', authedStaffId)
              Sentry.captureException(new Error("Force-close conversations safeguard triggered"))
            })
            return
          }

          if (conversationsToClose.length > 0) {
            console.log(`Auto closing ${conversationsToClose.length} conversations from past residents`)
            await Promise.all(
              conversationsToClose.map(con =>
                updateChatRoomStatus(
                  dispatch,
                  {id: con.id, isOnboardingChat: false, chatUsers: [{userId: con.conversationResidentId}]},
                  {userId: authedStaffId},
                  CONVERSATION_CLOSED,
                ),
              ),
            )
          }

          checkedForPastResidents = true
        }),
      )
    }

    // we skip the initial query if we haven't loaded residents yet
    pollables.current.forEach(p => p.startPolling())
  }, [activeBuildingId, authedStaffId, permissionsObject, hasLoadedResidents])

  // This effect controls playing a tone when a new chat message arrives.
  // if we do many more of these, we may want to split it into its own component
  // but I figured it made sense here [for now] because the backgroundDataSync is what initiates requests for new messages
  useEffect(() => {
    if (!permissionsObject.messagesRead) {
      return
    }

    if (
      // if our sounds are not muted
      !isMuted &&
      // if we had a previous value
      typeof previousLastMessageTimestamp.current === 'number' &&
      // and it doesn't match the new value
      previousLastMessageTimestamp.current < latestResidentMessageTimestamp &&
      // and we're not currently reading a conversation
      !matchPath(pathname, {path: Routes.MESSAGES_VIEW_SINGLE})
    ) {
      // play the new message tone
      playNewMessageTone()
    }

    // no matter what, we cache the last value
    previousLastMessageTimestamp.current = latestResidentMessageTimestamp
  }, [latestResidentMessageTimestamp, permissionsObject, pathname, isMuted])

  // don't show new message popups on the admin dashboard
  if (matchPath(pathname, {path: Routes.ADMIN_DASHBOARD})) {
    return null
  }

  return <NewMessages />
}

export default BackgroundDataSync
